<template>
  <div>
    <v-card
      class="mb-5"
      v-if="!isImmutable"
    >
      <v-card-text>
        <div class="d-flex align-center">
          <v-select
            dense hide-details
            class="mr-3"
            label="New data type"
            v-model="$data.$_newDataValueType"
            v-bind:items="$_valueTypeEnumItems"
            v-on:keydown.enter.stop
          >
          </v-select>

          <general-text-field
            dense hide-details
            class="mr-3"
            label="New data name"
            v-model="$data.$_newDataKey"
            v-on:keydown.enter="$_addNewDataMayFail"
          >
          </general-text-field>

          <v-btn
            icon x-small
            v-bind:disabled="$_isAddNewDataButtonDisabled"
            v-on:click="$_addNewData"
            v-on:keydown.enter.stop
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <div
      class="d-flex align-center"
      v-for="(dataKey, dataKeyIdx) of $data.$_dataKeys"
      v-bind:key="dataKeyIdx"
    >
      <input-holder
        class="d-flex flex-grow-0"
        v-bind:value="dataKey"
        v-bind:update="newDataKey => $_replaceDataKey(dataKeyIdx, newDataKey)"
      >
        <template v-slot:default="{ value, blur, change }">
          <general-text-field
            dense
            class="mr-3"
            v-bind:label="`Key(${$_dataValueTypes[dataKey]})`"
            v-bind:autofocus="dataKeyIdx === 0"
            v-bind:disabled="isImmutable"
            v-bind:value="value"
            v-on:blur="blur"
            v-on:change="change"
          >
          </general-text-field>
        </template>
      </input-holder>

      <input-holder
        class="d-flex flex-grow-1 mr-3"
        v-if="$_dataValueTypes[dataKey] === $_valueTypeEnum.string"
        v-bind:value="$_generalData[dataKey]"
        v-bind:update="value => $_updateData(dataKey, value)"
      >
        <template v-slot:default="{ value, blur, change }">
          <general-text-field
            dense label="Value"
            v-bind:disabled="isImmutable"
            v-bind:value="value"
            v-on:blur="blur"
            v-on:change="change"
          >
          </general-text-field>
        </template>
      </input-holder>

      <input-holder
        class="d-flex flex-grow-1 mr-3"
        v-else-if="$_dataValueTypes[dataKey] === $_valueTypeEnum.number"
        v-bind:value="$_generalData[dataKey]"
        v-bind:update="value => $_updateData(dataKey, Number(value))"
      >
        <template v-slot:default="{ value, blur, input }">
          <general-text-field
            dense label="Value" type="number"
            v-bind:disabled="isImmutable"
            v-bind:value="value"
            v-on:blur="blur"
            v-on:input="input"
          >
          </general-text-field>
        </template>
      </input-holder>

      <input-holder
        class="d-flex flex-grow-1 mr-3"
        v-else-if="$_dataValueTypes[dataKey] === $_valueTypeEnum.boolean"
        v-bind:value="$_generalData[dataKey]"
        v-bind:update="value => $_updateData(dataKey, value)"
      >
        <template v-slot:default="{ value, blur, change }">
          <v-checkbox
            dense label="Value"
            v-bind:input-value="value"
            v-on:blur="blur"
            v-on:change="change"
          >
          </v-checkbox>
        </template>
      </input-holder>

      <v-btn
        icon x-small
        class="flex-grow-0"
        v-on:click="$_deleteData(dataKeyIdx)"
        v-on:keydown.enter.stop
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import GeneralSegmentData from '../../../modules/segmentDataTypes/GeneralSegmentData.js';
import SegmentContentDataInputDefinition from '../../../modules/SegmentContentDataInputDefinition.js';
import InputHolder from './InputHolder.vue';
import GeneralTextField from './GeneralTextField.vue';
import { TimelineSegmentId } from '../../../modules/ScattData.js';

const valueTypeEnum = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
};

export const GeneralSegmentContentDataInputDefinition = new SegmentContentDataInputDefinition(
  'GeneralSegmentContentDataInput',
  GeneralSegmentData.contentData,
);

export default {
  components: {
    InputHolder,
    GeneralTextField,
  },
  
  model: {
    event: 'update',
    prop: 'timelineSegmentContentData'
  },

  props: {
    isImmutable:                { type: Boolean,                        required: true },
    timelineSegmentContentData: { type: GeneralSegmentData.contentData, required: true },
    timelineSegmentId:          { type: TimelineSegmentId,              required: true },
  },

  data() {
    return {
      $_dataKeys: [],
      $_newDataValueType: valueTypeEnum.string,
      $_newDataKey: '',
    };
  },

  watch: {
    timelineSegmentId: {
      handler() {
        this.$_updateDataKeys(Object.keys(this.$_generalData));
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    $_valueTypeEnumItems() {
      return Object.keys(valueTypeEnum).map(valueTypeId => ({
        text: valueTypeId,
        value: valueTypeId,
      }));
    },

    $_valueTypeEnum() {
      return valueTypeEnum;
    },

    $_isAddNewDataButtonDisabled() {
      if (Object.keys(this.$_generalData).includes(this.$data.$_newDataKey)) return true;
      if (this.$data.$_newDataKey.length === 0) return true;
      if (!Object.values(valueTypeEnum).includes(this.$data.$_newDataValueType)) return true;
      return false;
    },

    $_generalData: {
      get()            { return this.timelineSegmentContentData },
      set(generalData) { this.$emit('update', generalData) },
    },

    $_dataValueTypes() {
      let valueTypes = {};
      for (let [ dataKey, dataValue ] of Object.entries(this.$_generalData)) {
        if ((typeof dataValue === 'string') || (dataValue instanceof String)) {
          valueTypes[dataKey] = valueTypeEnum.string;
        } else if (typeof dataValue === 'number') {
          valueTypes[dataKey] = valueTypeEnum.number;
        } else if (typeof dataValue === 'boolean') {
          valueTypes[dataKey] = valueTypeEnum.boolean;
        }
      }
      return valueTypes;
    },
  },

  methods: {
    $_updateDataKeys(newDataKeys) {
      let updatedDataKeys = [];
      for (let dataKey of this.$data.$_dataKeys) {
        let isDataKeyRemaining = newDataKeys.includes(dataKey);
        if (isDataKeyRemaining) {
          updatedDataKeys.push(dataKey);
        }
      }
      for (let newDataKey of newDataKeys) {
        let isDataKeyAdded = !this.$data.$_dataKeys.includes(newDataKey);
        if (isDataKeyAdded) {
          updatedDataKeys.push(newDataKey);
        }
      }
      this.$data.$_dataKeys = updatedDataKeys;
    },

    $_replaceDataKey(dataKeyIdx, newDataKey) {
      if (newDataKey.length === 0) return false;
      if (this.$data.$_dataKeys.includes(newDataKey)) return false;
      let currentDataKey = this.$data.$_dataKeys[dataKeyIdx];
      this.$data.$_dataKeys[dataKeyIdx] = newDataKey;
      let generalData = this.$_generalData.clone();
      generalData[newDataKey] = generalData[currentDataKey];
      delete generalData[currentDataKey];
      this.$_generalData = generalData;
      return true;
    },

    $_updateData(dataKey, dataValue) {
      let generalData = this.$_generalData.clone();
      generalData[dataKey] = dataValue;
      this.$_generalData = generalData;
      return true;
    },

    $_addNewDataMayFail(event) {
      if (this.$_isAddNewDataButtonDisabled) return false;
      this.$_addNewData();
      event.stopPropagation();
      return true;
    },

    $_addNewData() {
      let generalData = this.$_generalData.clone();
      switch (this.$data.$_newDataValueType) {
        case valueTypeEnum.string:
          generalData[this.$data.$_newDataKey] = '';
          break;
        case valueTypeEnum.number:
          generalData[this.$data.$_newDataKey] = 0;
          break;
        case valueTypeEnum.boolean:
          generalData[this.$data.$_newDataKey] = false;
          break;
        default:
          return;
      }
      this.$_generalData = generalData;
      this.$data.$_dataKeys.push(this.$data.$_newDataKey);
      this.$data.$_newDataKey = '';
    },

    $_deleteData(dataKeyIdx) {
      let dataKey = this.$data.$_dataKeys[dataKeyIdx];
      this.$data.$_dataKeys.splice(dataKeyIdx, 1);
      let generalData = this.$_generalData.clone();
      delete generalData[dataKey];
      this.$_generalData = generalData;
    },
  },
}
</script>